import React, { useEffect, useMemo } from 'react';
import { PromiseState } from 'react-refetch';
import { connect } from 'react-redux';

import KPIWrapper from '../KPIWrapper/KPIWrapper';
import { compose } from 'recompose';
import { loadCountries } from 'redux/reducers/countries';

import { ReactComponent as TerritoryIcon } from 'icons/topics/territory.svg';

import mapTerritoryKPIData from '../mappings/mapTerritoryKPIData';
import apiConnect from 'helpers/apiConnect';
import getInitialSalesType from 'helpers/getInitialSalesType';
import getAllowedCustomerFilters from 'helpers/getAllowedCustomerFilters';
import getCustomerFilters from 'helpers/getCustomerFilters';

import useMedia from 'hooks/useMedia';

import { Container, KPIList, KPIListItem, Header, TWTDText } from '../shared';

import { withEntityContext } from 'context/entityContext';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';

import TopCountriesList from './TopCountriesList/TopCountriesList';
import TopCountry from './TopCountry/TopCountry';
import getTerritoryKPIURL from 'url-constructors/getTerritoryKPIURL';
import {selectAllCustomers, loadCustomers, isCustomersLoaded} from 'redux/reducers/customers';
import metrics from '../../../../maps/metrics';
import useCustomerFilters from 'hooks/useCustomerFilters';
import useExportURL from 'hooks/useExportURL';
import { RootState } from 'redux/rootReducer';
import { EntityTypes } from 'context/entityContext';
import { SalesTypeValues } from 'maps/sales-types';
import { ITopicFilterState } from 'components/TopicFilters/shared';
import { ICountryFlagProps } from 'types/shared';
import { IEntities } from 'components/data-containers/KPIs/types';
import { ICustomer } from 'redux/reducers/customers';
import { tooltipContent } from 'helpers/getTooltipContent';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import { IToolbarItemType } from 'components/utility/GenericComponent/GenericComponent';

interface ICustomerExtended extends ICustomer {
  thumbnailUrl: string;
}

interface ITerritoryKPIsProps {
  compareMode?: string;
  countries: {
    pending: boolean;
    fulfilled: boolean;
    rejected: boolean;
    reason: any;
    data: ICountryFlagProps[];
  };
  dataColors: string[];
  loadCountries: Function;
  loadCustomers: Function;
  topCountryThisWeekFetch: PromiseState<IEntities>;
  topCountryLastFullWeekFetch: PromiseState<IEntities>;
  topCountryReleaseToDateFetch: PromiseState<IEntities>;
  filters?: ITopicFilterState;
  countryIds: string[];
  entityIds: string[];
  isExternalUser: boolean;
  entityType: EntityTypes;
  glossarySlug: string;
}

/**
 * The DownloadKPI's component fetches data for the Territory KPI pages.
 * We purposefully use 3 seperate API calls, so we can use the API to do the heavy lifting
 * on sorting, filtering and limiting our data.
 *
 * The TopCountry component is an abstraction to only show the top 1 performing territory for both
 * release to date and this week to date values. In order for the data from the KPI to be suitable,
 * we process the results from the API through a custom mapping functions that returns flattened
 * arrays of territoriess.
 *
 * TopCountriesList shows a top 5 of countries per entity (track or country in country compare mode)
 * The data from the API does not need to be flattened in any way to be usable for this component,
 * as it maps neatly to the components flow.
 */
const TerritoryKPIs: React.FC<ITerritoryKPIsProps> = (props) => {
  const matchesSmall = useMedia('(min-width: 36em)');
  const matchesMedium = useMedia('(min-width: 45em)');
  const matchesLarge = useMedia('(min-width: 60em)');

  let columns = ['1fr'];
  if (matchesMedium) columns = ['1fr', '1fr'];
  if (matchesLarge) columns = ['1fr', '1fr', '1fr'];

  const {
    compareMode,
    countries,
    dataColors,
    loadCountries,
    loadCustomers,
    topCountryThisWeekFetch,
    topCountryLastFullWeekFetch,
    topCountryReleaseToDateFetch,
    filters,
    countryIds,
    entityIds,
    isExternalUser,
    entityType,
    glossarySlug,
  } = props;

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const compareArray = compareMode === 'country' ? countryIds : entityIds;

  const allFetches = PromiseState.all([
    topCountryThisWeekFetch,
    topCountryLastFullWeekFetch,
    topCountryReleaseToDateFetch,
  ]);

  const isNotStartLoaded = (response: Response): boolean => !response || response?.url === window.location.href

  const [pending, fulfilled, rejected, reason] = useMemo(() => {
    const isNotStartLoading = isNotStartLoaded(topCountryReleaseToDateFetch.meta?.request) ||
      isNotStartLoaded(topCountryLastFullWeekFetch.meta?.request) ||
      isNotStartLoaded(topCountryThisWeekFetch.meta?.request);

    return [
      isNotStartLoading ? true : allFetches.pending || countries.pending,
      isNotStartLoading ? false : allFetches.fulfilled && countries.fulfilled,
      isNotStartLoading ? false : allFetches.rejected || countries.rejected,
      isNotStartLoading ? false : allFetches.reason || countries.reason,
    ]
  }, [allFetches, countries, topCountryThisWeekFetch, topCountryLastFullWeekFetch, topCountryReleaseToDateFetch])


  const [
    sortedTopCountryThisWeek,
    sortedTopCountryLastFullWeek,
    sortedTopCountryReleaseToDate,
  ] = useMemo(() => {
    const metricValues = [
      topCountryThisWeekFetch.value,
      topCountryLastFullWeekFetch.value,
      topCountryReleaseToDateFetch.value,
    ];

    /**
     * Sorts items based on their entity id.
     * @param {object} a entity a
     * @param {object} b entity b
     */
    function byEntityId(a: IEntities, b: IEntities): number {
      return entityIds.indexOf(a.entity.id) - entityIds.indexOf(b.entity.id);
    }

    // Map through the api data, and return sorted values if the data is loaded.
    return metricValues.map((metric) => {
      if (metric) {
        return metric.sort(byEntityId);
      }

      return metric;
    });
  }, [
    entityIds,
    topCountryLastFullWeekFetch.value,
    topCountryReleaseToDateFetch.value,
    topCountryThisWeekFetch.value,
  ]);

  /**
   * You need this to group everything by country for the normal views and country compares
   * It's quite possible that entity compare does not need a mapping at all!
   */
  const topCountryThisWeekToDateMappedData = mapTerritoryKPIData({
    data: sortedTopCountryThisWeek,
    entityIds,
    countryIds,
    compareMode,
    countryFilter: filters?.country,
    region: filters?.region,
  });

  const topCountryLastFullWeekMappedData = mapTerritoryKPIData({
    data: sortedTopCountryLastFullWeek,
    entityIds,
    countryIds,
    compareMode,
    countryFilter: filters?.country,
    region: filters?.region,
  });

  const topCountryReleaseToDateMappedData = mapTerritoryKPIData({
    data: sortedTopCountryReleaseToDate,
    entityIds,
    countryIds,
    compareMode,
    countryFilter: filters?.country,
    region: filters?.region,
  });

  const salesType = getInitialSalesType(isExternalUser, entityType);
  const [allowedCustomerFilters] = useCustomerFilters(
    salesType as SalesTypeValues
  );
  const allowedFilters = [
    'country',
    ...allowedCustomerFilters,
    'media',
    'region',
    `customRegion`,
  ];

  // Only show the TopCountriesList in non-compare mode, or in entity compare mode with no country filter set.
  let showTopCountriesList = true;
  if (compareMode === 'country') {
    showTopCountriesList = false;
  } else if (compareMode === 'entity' && filters?.country) {
    showTopCountriesList = false;
  }

  return (
    <Container data-pw="territory-kpis">
      <ErrorBoundary error={reason} errorInfo={rejected ? reason?.cause : null}>
        <KPIWrapper
          title="Territory KPIs"
          icon={TerritoryIcon}
          allowedFilters={allowedFilters}
          glossarySlug={glossarySlug}
          extraToolbarItems={[
            {
              label: 'Export week to date data',
              href: useExportURL(topCountryThisWeekFetch, {
                limit: 999,
              }),
              type: IToolbarItemType.Export,
              order: 1,
            },
            {
              label: 'Export release to date data',
              href: useExportURL(topCountryReleaseToDateFetch, {
                limit: 999,
              }),
              type: IToolbarItemType.Export,
              order: 2,
            },
          ]}
          titleAlignLeft="-1.5em"
          alignSVG="2em"
        >
          <KPIList columns={columns} data-testid="KPIList" marginTop="30px">
            <KPIListItem>
              <Header>
                <TippyToolTip
                  enabled
                  tooltipContent={tooltipContent}
                  offset={[10, 25]}
                  maxWidth="170px"
                >
                  <TWTDText>{metrics['twtd'].label}</TWTDText>
                </TippyToolTip>
              </Header>
              <TopCountry
                data={topCountryThisWeekToDateMappedData as any}
                countryFlags={countries.data}
                pending={pending}
                fulfilled={fulfilled}
                dataColors={dataColors}
                compareMode={compareMode}
                compareArray={compareArray}
                sortMetric={'twtd'}
                salesType={salesType}
                isLargeScreen={matchesSmall}
              />
              {showTopCountriesList && (
                <TopCountriesList
                  entities={sortedTopCountryThisWeek}
                  countryFlags={countries.data}
                  compareMode={compareMode}
                  dataColors={dataColors}
                  compareArray={compareArray}
                  highlight={filters?.country}
                  fulfilled={fulfilled}
                  sortMetric={'twtd'}
                  hasListToggle={matchesMedium}
                />
              )}
            </KPIListItem>

            <KPIListItem>
              <Header>{metrics['lw'].label}</Header>
              <TopCountry
                data={topCountryLastFullWeekMappedData as any}
                countryFlags={countries.data}
                pending={pending}
                fulfilled={fulfilled}
                dataColors={dataColors}
                compareMode={compareMode}
                compareArray={compareArray}
                sortMetric={'lw'}
                salesType={salesType}
                isLargeScreen={matchesSmall}
              />
              {showTopCountriesList && (
                <TopCountriesList
                  entities={sortedTopCountryLastFullWeek}
                  countryFlags={countries.data}
                  compareMode={compareMode}
                  dataColors={dataColors}
                  compareArray={compareArray}
                  highlight={filters?.country}
                  fulfilled={fulfilled}
                  sortMetric={'lw'}
                  hasListToggle={matchesMedium}
                />
              )}
            </KPIListItem>

            <KPIListItem>
              <Header>{metrics['rtd'].label}</Header>
              <TopCountry
                data={topCountryReleaseToDateMappedData as any}
                countryFlags={countries.data}
                pending={pending}
                fulfilled={fulfilled}
                dataColors={dataColors}
                compareMode={compareMode}
                compareArray={compareArray}
                sortMetric={'rtd'}
                salesType={salesType}
                isLargeScreen={matchesSmall}
              />
              {showTopCountriesList && (
                <TopCountriesList
                  entities={sortedTopCountryReleaseToDate}
                  countryFlags={countries.data}
                  compareMode={compareMode}
                  dataColors={dataColors}
                  compareArray={compareArray}
                  highlight={filters?.country}
                  fulfilled={fulfilled}
                  sortMetric={'rtd'}
                  hasListToggle={matchesMedium}
                />
              )}
            </KPIListItem>
          </KPIList>
        </KPIWrapper>
      </ErrorBoundary>
    </Container>
  );
};

interface IApiConnectProps {
  entityType: EntityTypes;
  entityIds: string[];
  countryIds: string[];
  isExternalUser: boolean;
  filters?: ITopicFilterState;
  customers: ICustomerExtended[];
  compareMode?: string;
  customRegionFilter: any;
  isCustomersLoaded?: boolean;
}

export default compose<IApiConnectProps, any>(
  withEntityContext,
  connect(
    (state: RootState) => ({
      countries: state.countries,
      customers: selectAllCustomers(state),
      isCustomersLoaded: isCustomersLoaded(state),
    }),
    { loadCountries, loadCustomers }
  ),
  apiConnect(
    ({
      entityType,
      entityIds,
      countryIds,
      isExternalUser,
      filters = {} as ITopicFilterState,
      customers,
      compareMode,
      customRegionFilter,
      isCustomersLoaded,
    }: IApiConnectProps): any => {
      const initialSalesType = getInitialSalesType(isExternalUser, entityType);

      const availableCustomerFilters = getAllowedCustomerFilters(
        initialSalesType as any,
        entityType as any
      );

      const isCustomerFilterApplied = availableCustomerFilters.some((el: any) => !!(filters as any)?.[el])

      const customerFilter = getCustomerFilters(
        customers,
        availableCustomerFilters,
        filters
      );

      /**
       * Limit effects the number of items returned per entity.
       * It does not limit the number of entities (country/entity compare) you can retrieve.
       */
      let limit = 5;
      if (compareMode === 'country') {
        limit = countryIds.length;
      }

      const getUrl = (args: {
        sortMetric: 'twtd' | 'rtd' | 'lw';
        limit: number;
      }): string =>
        (isCustomerFilterApplied && isCustomersLoaded || !isCustomerFilterApplied) ?
        getTerritoryKPIURL({
          entityIds,
          entityType,
          sortOrder: 'desc',
          countryIds: compareMode === 'country' ? countryIds : filters.country,
          customerFilter,
          salesType: initialSalesType,
          mediaTypes: filters?.media,
          region: filters?.region,
          customRegion: customRegionFilter,
          ...args,
        }) : '';

      return {
        topCountryThisWeekFetch: getUrl({
          sortMetric: 'twtd',
          limit,
        }),
        topCountryLastFullWeekFetch: getUrl({
          sortMetric: 'lw',
          limit,
        }),
        topCountryReleaseToDateFetch: getUrl({
          sortMetric: 'rtd',
          limit,
        }),
      };
    }
  )
)(TerritoryKPIs as any);
